import { useEffect, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useSupport } from "@/context/SupportContext";
import { LifebuoyIcon, PaperAirplaneIcon, CheckCircleIcon, ArrowPathIcon, UsersIcon, BoltIcon, PlusIcon, TrashIcon } from "@heroicons/react/24/outline";

const timeShort = (iso) => { try { return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };
const initials = (n) => (n || "C").trim().charAt(0).toUpperCase();

// Manage which staff can answer support chats (admin only).
const SupportAgentsDialog = () => {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);

  const load = () => api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { if (open) load(); }, [open]);

  const toggle = async (u) => {
    try {
      const { data } = await api.post(`/admin/support-agents/${u.user_id}/toggle`);
      setUsers((prev) => prev.map((x) => (x.user_id === u.user_id ? { ...x, support_agent: data.support_agent } : x)));
      toast.success(`${u.name || u.email} ${data.support_agent ? "is now a support agent" : "removed as support agent"}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const filtered = users.filter((u) => u.role !== "admin" && `${u.name || ""} ${u.email}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid="manage-agents-btn"><UsersIcon className="w-4 h-4 mr-1.5" />Agents</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Support agents</DialogTitle>
          <DialogDescription>Admins can always answer. Promote staff members below to let them reply to customer-care chats too.</DialogDescription>
        </DialogHeader>
        <Input data-testid="agents-search" placeholder="Search by name or email" value={q} onChange={(e) => setQ(e.target.value)} />
        <div className="max-h-80 overflow-y-auto divide-y">
          {filtered.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">No matching users.</p>}
          {filtered.map((u) => (
            <div key={u.user_id} className="flex items-center gap-3 py-2.5" data-testid={`agent-row-${u.user_id}`}>
              <span className="w-9 h-9 rounded-full bg-muted flex items-center justify-center font-bold shrink-0">{initials(u.name || u.email)}</span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">{u.name || "—"}</p>
                <p className="text-xs text-muted-foreground truncate">{u.email} · {u.role}</p>
              </div>
              <Switch data-testid={`agent-toggle-${u.user_id}`} checked={!!u.support_agent} onCheckedChange={() => toggle(u)} />
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const AdminSupportInbox = () => {
  const { user } = useAuth();
  const { threads, loadThreads, openThread, activeId, messages, send, threadAction } = useSupport();
  const [status, setStatus] = useState("open");
  const [draft, setDraft] = useState("");
  const [canned, setCanned] = useState([]);
  const [newCanned, setNewCanned] = useState({ title: "", text: "" });
  const scrollRef = useRef(null);
  const isAdmin = user?.role === "admin";

  useEffect(() => { loadThreads(status); }, [status, loadThreads]);
  useEffect(() => { api.get("/support/canned").then((r) => setCanned(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages, activeId]);

  const active = threads.find((t) => t.thread_id === activeId);
  const submit = (e) => { e.preventDefault(); const t = draft.trim(); if (!t) return; send(t); setDraft(""); };
  const insertCanned = (text) => setDraft((d) => (d ? `${d} ${text}` : text));

  const addCanned = async () => {
    if (!newCanned.title.trim() || !newCanned.text.trim()) return toast.error("Title and text required");
    try {
      const { data } = await api.post("/support/canned", newCanned);
      setCanned((c) => [...c, data]); setNewCanned({ title: "", text: "" }); toast.success("Saved reply added");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const deleteCanned = async (id) => {
    try { await api.delete(`/support/canned/${id}`); setCanned((c) => c.filter((x) => x.canned_id !== id)); } catch { toast.error("Failed"); }
  };

  return (
    <Card className="p-0 overflow-hidden" data-testid="admin-support-inbox">
      <div className="grid md:grid-cols-[320px_1fr] h-[72vh] min-h-[480px]">
        <div className="border-r border-border/60 flex flex-col">
          <div className="px-4 py-3 border-b flex items-center gap-2">
            <LifebuoyIcon className="w-5 h-5 text-primary" />
            <span className="font-semibold text-sm">Support</span>
            <div className="ml-auto flex gap-1">
              {["open", "closed", "all"].map((s) => (
                <button key={s} data-testid={`support-filter-${s}`} onClick={() => setStatus(s)}
                  className={`text-xs px-2 py-1 rounded-full capitalize ${status === s ? "bg-primary text-white" : "text-muted-foreground hover:bg-muted"}`}>{s}</button>
              ))}
            </div>
          </div>
          {isAdmin && <div className="px-3 py-2 border-b"><SupportAgentsDialog /></div>}
          <div className="flex-1 overflow-y-auto">
            {threads.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground"><LifebuoyIcon className="w-10 h-10 mx-auto mb-3 opacity-30" />No {status} conversations.</div>}
            {threads.map((t) => (
              <button key={t.thread_id} data-testid={`support-thread-${t.thread_id}`} onClick={() => openThread(t.thread_id)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left border-b border-border/40 transition-colors ${activeId === t.thread_id ? "bg-orange-50" : "hover:bg-muted/50"}`}>
                <span className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-amber-500 text-white flex items-center justify-center font-bold shrink-0">{initials(t.customer_name)}</span>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-sm truncate">{t.customer_name}</p>
                  <p className="text-xs text-muted-foreground truncate">{t.last_message || "New conversation"}</p>
                </div>
                {t.status === "closed" && <span className="text-[10px] text-muted-foreground">closed</span>}
                {t.unread > 0 && <span data-testid={`support-thread-unread-${t.thread_id}`} className="min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[11px] font-bold flex items-center justify-center">{t.unread}</span>}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col">
          {!active ? (
            <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground p-8 text-center">
              <LifebuoyIcon className="w-12 h-12 mb-3 opacity-30" /><p className="text-sm">Select a conversation to reply</p>
            </div>
          ) : (
            <>
              <div className="px-4 py-3 border-b flex items-center gap-3">
                <span className="w-9 h-9 rounded-full bg-gradient-to-br from-primary to-amber-500 text-white flex items-center justify-center font-bold">{initials(active.customer_name)}</span>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-sm truncate">{active.customer_name}</p>
                  <p className="text-xs text-muted-foreground">{active.assigned_name ? `Assigned to ${active.assigned_name}` : "Unassigned"}</p>
                </div>
                {active.status === "open"
                  ? <Button data-testid="support-close-btn" size="sm" variant="outline" onClick={() => threadAction(active.thread_id, "close")}><CheckCircleIcon className="w-4 h-4 mr-1" />Close</Button>
                  : <Button data-testid="support-reopen-btn" size="sm" variant="outline" onClick={() => threadAction(active.thread_id, "reopen")}><ArrowPathIcon className="w-4 h-4 mr-1" />Reopen</Button>}
              </div>
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-muted/20">
                {messages.map((m) => {
                  const mine = m.sender_role === "support";
                  return (
                    <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] rounded-2xl px-3.5 py-2 text-sm ${mine ? "bg-primary text-white rounded-br-sm" : "bg-white dark:bg-card border rounded-bl-sm"}`}>
                        <p className="whitespace-pre-wrap break-words">{m.text}</p>
                        <p className={`text-[10px] mt-0.5 ${mine ? "text-white/70" : "text-muted-foreground"}`}>{m.sender_name} · {timeShort(m.created_at)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              {/* Canned replies */}
              <div className="px-3 pt-2 flex items-center gap-2 flex-wrap border-t">
                <BoltIcon className="w-4 h-4 text-primary shrink-0" />
                <div className="flex-1 flex gap-1.5 overflow-x-auto py-1">
                  {canned.map((c) => (
                    <button key={c.canned_id} type="button" data-testid={`canned-${c.canned_id}`} onClick={() => insertCanned(c.text)}
                      title={c.text} className="text-xs whitespace-nowrap px-2.5 py-1 rounded-full border bg-muted/50 hover:bg-orange-50 hover:border-primary transition-colors">
                      {c.title}
                    </button>
                  ))}
                </div>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button size="sm" variant="ghost" data-testid="manage-canned-btn" className="shrink-0"><PlusIcon className="w-4 h-4" /></Button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="w-80 space-y-2">
                    <p className="text-sm font-semibold">Saved replies</p>
                    <div className="max-h-40 overflow-y-auto space-y-1">
                      {canned.map((c) => (
                        <div key={c.canned_id} className="flex items-center gap-2 text-xs">
                          <span className="flex-1 truncate" title={c.text}>{c.title}</span>
                          <button data-testid={`canned-delete-${c.canned_id}`} onClick={() => deleteCanned(c.canned_id)} className="text-red-500 hover:text-red-700"><TrashIcon className="w-3.5 h-3.5" /></button>
                        </div>
                      ))}
                    </div>
                    <Input data-testid="canned-title-input" placeholder="Title (e.g. Refund status)" value={newCanned.title} onChange={(e) => setNewCanned({ ...newCanned, title: e.target.value })} className="h-8 text-xs" />
                    <Textarea data-testid="canned-text-input" placeholder="Reply text…" value={newCanned.text} onChange={(e) => setNewCanned({ ...newCanned, text: e.target.value })} rows={3} className="text-xs" />
                    <Button size="sm" data-testid="canned-add-btn" onClick={addCanned} className="w-full">Add saved reply</Button>
                  </PopoverContent>
                </Popover>
              </div>
              <form onSubmit={submit} className="p-3 pt-2 flex items-center gap-2">
                <Input data-testid="support-reply-input" value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Type your reply…" className="rounded-full" />
                <Button type="submit" size="icon" className="rounded-full shrink-0" data-testid="support-reply-send" aria-label="Send"><PaperAirplaneIcon className="w-5 h-5" /></Button>
              </form>
            </>
          )}
        </div>
      </div>
    </Card>
  );
};
